import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { LogoWithText } from "../components/Logo";
import { updatePageTitle } from "../utils/titleUtils";
import { FORGOT_PASSWORD } from "../api/apiFunction/authServices";
import AuthShowcase from "../components/pages/auth/AuthShowcase";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    updatePageTitle("Forgot Password");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate email
    if (!email) {
      toast.error("Email address is required");
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      const response = await FORGOT_PASSWORD({ email });

      if (response.status === 200) {
        setIsSubmitted(true);
        toast.success("Password reset instructions sent to your email");
      } else {
        const errorMessage =
          response?.data?.error ||
          response?.data?.message ||
          "Failed to send reset instructions";
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to send reset instructions";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-bg-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <LogoWithText className="h-8 w-auto mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-fg-50 mb-2">
              Check Your Email
            </h2>
            <p className="text-fg-60 text-sm">
              We've sent password reset instructions to <strong>{email}</strong>
            </p>
          </div>

          {/* Success Card */}
          <div className="bg-bg-60 rounded-2xl p-8 border border-bd-50 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-fg-60 text-sm mb-6">
              Didn't receive the email? Check your spam folder or try again.
            </p>
            <button
              onClick={() => setIsSubmitted(false)}
              className="text-ac-02 hover:text-ac-01 text-sm font-medium"
            >
              Try again
            </button>
          </div>

          {/* Back to Login */}
          <div className="text-center mt-6">
            <Link
              to="/login"
              className="text-fg-60 hover:text-fg-50 text-sm font-medium"
            >
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-bg-50 flex overflow-hidden">
      {/* Left Side - Auth Form */}
      <div className="w-full lg:w-1/2 bg-bg-50 p-4 lg:p-8">
        <div className="bg-bg-70 rounded-3xl h-full flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md space-y-8">
            {/* Logo */}
            <div className="text-center">
              <LogoWithText className="h-8 w-auto mx-auto mb-8" />
              <div className="leading-tight">
                <h2 className="text-xl font-semibold text-fg-50 mb-1">
                  Forgot your password?
                </h2>
                <p className="text-xl font-semibold text-fg-50">
                  We'll help you reset it.
                </p>
              </div>
            </div>

            {/* Form */}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                  placeholder="you@example.com"
                  value={email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-2 px-4 rounded-lg text-sm font-medium text-white dark:text-black ${
                  loading
                    ? "bg-ac-02 bg-opacity-25 cursor-not-allowed"
                    : "bg-ac-02 hover:bg-ac-01"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ac-02`}
              >
                {loading ? "Sending..." : "Submit"}
              </button>
            </form>

            {/* Remember Password Link */}
            <div className="text-center">
              <p className="text-sm text-fg-60">
                Remember Password?{" "}
                <Link
                  to="/login"
                  className="font-medium text-fg-50 hover:text-fg-40"
                >
                  Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Showcase */}
      <AuthShowcase />
    </div>
  );
};

export default ForgotPassword;
